import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Clock, CalendarDays, Banknote, TrendingUp,
  GraduationCap, FileText, UserPlus, ShieldCheck, Landmark, Car,
  FileSpreadsheet, Building2, Building, Package, CheckSquare, Percent,
  FolderKanban, ShoppingCart, Truck, Timer, BarChart3, PieChart,
  UserCog, ScrollText, Settings, X, ChevronDown, ChevronRight,
  GitMerge, Trophy, Receipt, Wallet, Warehouse, Navigation, Factory, Bell, AlertTriangle,
  Target, BookOpen, BookMarked, Layers, Scale, Calculator, SlidersHorizontal, ArrowLeftRight, KeyRound, Gauge,
  GitBranch, History, Table2, LayoutGrid,
  Grid3X3, ListChecks, PackageCheck, Boxes,
  ArrowDownCircle, ArrowUpCircle, FileWarning, BookCheck, BookType,
  CalendarCheck, CalendarClock, CalendarMinus, Send,
  Settings2, Wand2,
  Monitor, Activity, ClipboardCheck,
  Coins, Library, Milestone,
  BrainCircuit, PackagePlus, Crosshair,
  Globe, Repeat, Megaphone,
  Workflow, Blocks, Code2,
  Award, Briefcase, CalendarRange, Combine, Compass, Container,
  CreditCard, DollarSign, FolderOpen, Leaf, Plug, PlugZap,
  Radar, ScanBarcode, ShieldAlert, Ship, Star, Wrench,
  AlertOctagon, Calendar, ClipboardList, Cpu, Database, FileBox, FileCode,
  FormInput, GanttChart, HardHat, Headphones, Key, Menu,
  MessageCircle, MessageSquare, Paintbrush, PenTool, RotateCcw, Ruler,
  ScanLine, Scissors, Shield, Tag,
  Clock3, CalendarHeart, Network, Brain, UserCheck, MapPin, BarChart,
  SearchCheck, PackageX, TrendingDown, Anchor, FileSearch, FileStack, FileCheck,
  BadgeCheck, BellRing, FileBarChart, Gavel, GitPullRequest, Presentation, UsersRound, Webhook,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useModules } from '../../hooks/useModules';
import { getVisibleSections, getVisibleSectionsByPermissionFn } from '../../config/navigation';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard, Users, Clock, CalendarDays, Banknote, TrendingUp,
  GraduationCap, FileText, UserPlus, ShieldCheck, Landmark, Car,
  FileSpreadsheet, Building2, Building, Package, CheckSquare, Percent,
  FolderKanban, ShoppingCart, Truck, Timer, BarChart3, PieChart,
  UserCog, ScrollText, Settings, GitMerge, Trophy, Receipt, Wallet,
  Warehouse, Navigation, Factory, Bell, AlertTriangle,
  Target, BookOpen, BookMarked, Layers, Scale, Calculator, SlidersHorizontal, ArrowLeftRight, KeyRound, Gauge,
  GitBranch, History, Table2, LayoutGrid,
  Grid3X3, ListChecks, PackageCheck, Boxes,
  ArrowDownCircle, ArrowUpCircle, FileWarning, BookCheck, BookType,
  CalendarCheck, CalendarClock, CalendarMinus, Send,
  Settings2, Wand2,
  Monitor, Activity, ClipboardCheck,
  Coins, Library, Milestone,
  BrainCircuit, PackagePlus, Crosshair,
  Globe, Repeat, Megaphone,
  Workflow, Blocks, Code2,
  Award, Briefcase, CalendarRange, Combine, Compass, Container,
  CreditCard, DollarSign, FolderOpen, Leaf, Plug, PlugZap,
  Radar, ScanBarcode, ShieldAlert, Ship, Star, Wrench,
  AlertOctagon, Calendar, ClipboardList, Cpu, Database, FileBox, FileCode,
  FormInput, GanttChart, HardHat, Headphones, Key, Menu,
  MessageCircle, MessageSquare, Paintbrush, PenTool, RotateCcw, Ruler,
  ScanLine, Scissors, Shield, Tag,
  Clock3, CalendarHeart, Network, Brain, UserCheck, MapPin, BarChart,
  SearchCheck, PackageX, TrendingDown, Anchor, FileSearch, FileStack, FileCheck,
  BadgeCheck, BellRing, FileBarChart, Gavel, GitPullRequest, Presentation, UsersRound, Webhook,
};

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  desktopCollapsed: boolean;
  onNavClick: () => void;
}

export default function Sidebar({ mobileOpen, onMobileClose, desktopCollapsed, onNavClick }: SidebarProps) {
  const { profile } = useAuth();
  const { activeCompany, isHoldingView } = useCompany();
  const { hasPermission, loadingPermissions } = usePermissions();
  const { isModuleEnabled, loadingModules } = useModules();
  const location = useLocation();
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(['Overview']));
  const [hasInitialized, setHasInitialized] = useState(false);

  if (!profile) return null;

  // Navigation: DB-permission-driven + module-aware.
  // While permissions or modules are loading, fall back to role-based nav.
  const roleSections = getVisibleSections(profile.role);
  const ready = !loadingPermissions && !loadingModules;
  const permSections = ready
    ? getVisibleSectionsByPermissionFn(hasPermission, isModuleEnabled)
    : null;

  const sections = permSections ?? roleSections;

  // Auto-expand the section containing the active route on first render
  if (!hasInitialized && sections.length > 0) {
    const activeSection = sections.find(s =>
      s.items.some(item => item.path.split('?')[0] === location.pathname)
    );
    if (activeSection && !expandedSections.has(activeSection.title)) {
      setExpandedSections(prev => new Set([...prev, activeSection.title]));
    }
    setHasInitialized(true);
  }

  const toggleSection = (title: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`
          fixed top-0 start-0 h-full w-64 bg-slate-50 dark:bg-slate-950 border-e border-slate-200 dark:border-slate-800 z-30
          flex flex-col transition-all duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0 rtl:translate-x-0' : '-translate-x-full rtl:translate-x-full'}
          lg:static lg:z-auto
          ${desktopCollapsed ? 'lg:-translate-x-full lg:rtl:translate-x-full lg:w-0 lg:border-0 lg:overflow-hidden' : 'lg:translate-x-0 lg:rtl:translate-x-0 lg:w-64'}
        `}
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-white overflow-hidden">
              <img src="/assets/logo.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div className="min-w-0">
              <p className="text-slate-900 dark:text-white text-sm font-semibold leading-none truncate">
                {activeCompany?.company_name || t('sidebar_erp_system')}
              </p>
              <p className={`text-xs mt-0.5 ${isHoldingView ? 'text-amber-800 dark:text-amber-500' : 'text-slate-600 dark:text-slate-500'}`}>
                {isHoldingView ? t('header_consolidated_view') : t('sidebar_erp_system')}
              </p>
            </div>
          </div>
          <button onClick={onMobileClose} className="lg:hidden text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors flex-shrink-0 ms-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {sections.map((section, idx) => {
            const isCollapsed = !expandedSections.has(section.title);
            // Phase 1: render a domain header when the domain changes between adjacent sections
            const prevDomain = idx > 0 ? sections[idx - 1].domain : undefined;
            const showDomainHeader = !!section.domain && section.domain !== prevDomain;
            return (
              <div key={section.title} className="mb-1">
                {showDomainHeader && section.domainKey && (
                  <div className={`${idx === 0 ? 'mt-0' : 'mt-4'} mb-2 px-3 pt-3 ${idx === 0 ? '' : 'border-t border-slate-200/50 dark:border-slate-800/50'}`}>
                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-600 uppercase tracking-[0.15em]">
                      {t(section.domainKey)}
                    </span>
                  </div>
                )}
                {section.title !== 'Overview' && (
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center gap-1 px-3 py-1.5 mb-1 group"
                  >
                    <span className="flex-1 min-w-0 truncate text-[11px] font-semibold text-slate-600 dark:text-slate-500 uppercase tracking-wider leading-none group-hover:text-slate-500 dark:group-hover:text-slate-400 transition-colors">
                      {t(section.titleKey)}
                    </span>
                    {isCollapsed
                      ? <ChevronRight className="w-3 h-3 text-slate-700 dark:text-slate-600 flex-shrink-0 rtl:rotate-180" />
                      : <ChevronDown className="w-3 h-3 text-slate-700 dark:text-slate-600 flex-shrink-0" />
                    }
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.filter(item => !item.companyIds || item.companyIds.includes(activeCompany?.id ?? '')).map(item => {
                      const Icon = iconMap[item.icon];
                      const isActive = location.pathname === item.path.split('?')[0];
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={onNavClick}
                          className={`
                            flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150
                            ${isActive
                              ? 'bg-blue-600/15 text-blue-400 border border-blue-500/20'
                              : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                            }
                          `}
                        >
                          {Icon && <Icon className={`w-[15px] h-[15px] flex-shrink-0 ${isActive ? 'text-blue-400' : ''}`} />}
                          <span className="flex-1 min-w-0 truncate leading-tight">{t(item.labelKey)}</span>
                          {item.beta && (
                            <span className="text-[9px] px-1 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30 rounded flex-shrink-0 font-semibold">
                              BETA
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-slate-300 dark:from-slate-600 to-slate-200 dark:to-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-slate-900 dark:text-white text-xs font-bold">
                {profile.full_name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{profile.full_name}</p>
              <p className="text-slate-600 dark:text-slate-500 text-xs truncate capitalize">{t(`role_${profile.role}`, { defaultValue: profile.role.replace(/_/g, ' ') })}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
